import React, { useState, useRef, useEffect } from 'react';
import { Contact } from '../types';
import { User, Phone, Mail, MoreVertical, Pencil, Trash2 } from 'lucide-react';
import { cn } from '../lib/utils';
import { useLanguage } from '../functions/useLanguage';

interface ContactCardProps {
  contact: Contact;
  onDelete: () => void;
  onEdit: () => void;
}

export const ContactCard: React.FC<ContactCardProps> = ({ contact, onDelete, onEdit }) => {
  const { t } = useLanguage();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const normalizedRole = contact.role === 'Nephrologist' ? 'Vet' : contact.role;
  const roleLabel =
    normalizedRole === 'Vet'
      ? t('contacts.nephrologist')
      : normalizedRole === 'Pharmacy'
      ? t('contacts.pharmacy')
      : normalizedRole === 'Emergency'
      ? t('contacts.emergency')
      : t('contacts.other');

  // Close on outside click
  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen]);

  return (
    <div className={cn("p-4 rounded-xl border flex justify-between items-center", "bg-white border-gray-100")}>
      <div className="flex items-center gap-3">
        <div className={cn("p-2 rounded-full", "bg-blue-100 text-blue-600")}>
          <User size={20} />
        </div>
        <div>
          <h4 className={cn("font-bold", "text-text-primary")}>{contact.name}</h4>
          <p className={cn("text-xs uppercase tracking-wide", "text-text-secondary")}>{roleLabel}</p>
          <div className={cn("flex flex-wrap items-center gap-x-1 gap-y-0.5 mt-0.5 text-xs", "text-gray-400")}>
            <span>{contact.phone_number}</span>
            {contact.email && <><span className="text-gray-300">•</span><span>{contact.email}</span></>}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-1">
        <a
          href={`tel:${contact.phone_number}`}
          className={cn("p-2 rounded-full transition-colors", "bg-green-100 text-positive-green hover:bg-green-200")}
        >
          <Phone size={18} />
        </a>
        {contact.email && (
          <a
            href={`mailto:${contact.email}`}
            className={cn("p-2 rounded-full transition-colors", "bg-blue-100 text-blue-600 hover:bg-blue-200")}
          >
            <Mail size={18} />
          </a>
        )}

        {/* Kebab menu */}
        <div ref={menuRef} className="relative">
          <button
            onClick={() => setMenuOpen(v => !v)}
            className={cn("p-2 rounded-full transition-colors", "text-gray-400 hover:bg-gray-100")}
          >
            <MoreVertical size={18} />
          </button>

          {menuOpen && (
            <div className={cn(
              "absolute right-0 top-full mt-1 w-36 rounded-xl shadow-lg border overflow-hidden z-50",
              "bg-white border-gray-100"
            )}>
              <button
                onClick={() => { setMenuOpen(false); onEdit(); }}
                className={cn(
                  "w-full flex items-center gap-2.5 px-4 py-3 text-sm font-medium transition-colors",
                  "text-text-secondary hover:bg-gray-50"
                )}
              >
                <Pencil size={15} />
                {t('contacts.edit')}
              </button>
              <button
                onClick={() => { setMenuOpen(false); onDelete(); }}
                className={cn(
                  "w-full flex items-center gap-2.5 px-4 py-3 text-sm font-medium transition-colors border-t",
                  "text-error-red hover:bg-error-red\/5 border-gray-100"
                )}
              >
                <Trash2 size={15} />
                {t('contacts.delete')}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
